import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { HiOutlineX } from "react-icons/hi";
import { cn } from "@/lib/cn";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[10000] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { closable?: boolean }
>(({ className, children, closable = true, "aria-describedby": ariaDescribedBy, ...props }, ref) => {
  // Radix emits a Description-missing warning when neither a DialogDescription
  // component nor an explicit aria-describedby is provided. Generate a stable
  // fallback id and attach a visually-hidden sr-only description so every
  // dialog in the app is a11y-complete without each call site managing it.
  const fallbackId = React.useId();
  const describedBy = ariaDescribedBy ?? fallbackId;

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={describedBy}
        className={cn(
          "fixed left-[50%] top-[50%] z-[10001] flex flex-col w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-[var(--lilia-border)] bg-[var(--lilia-surface)] p-6 shadow-xl duration-200 overflow-y-auto max-h-[90vh] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-xl",
          className,
        )}
        {...props}
        // A toast's buttons (Undo, Open) aren't "outside": pressing one keeps the dialog open.
        onPointerDownOutside={(e) => {
          if ((e.target as Element | null)?.closest?.("[data-lilia-toast]")) e.preventDefault();
          props.onPointerDownOutside?.(e);
        }}
        onInteractOutside={(e) => {
          if ((e.target as Element | null)?.closest?.("[data-lilia-toast]")) e.preventDefault();
          props.onInteractOutside?.(e);
        }}
      >
        {children}
        {/* Visually-hidden fallback description — silences Radix's a11y
            warning when no <DialogDescription> is provided. If the caller
            passes aria-describedby explicitly, this renders but isn't
            referenced. */}
        {!ariaDescribedBy && (
          <span id={fallbackId} className="sr-only">Dialog</span>
        )}
        {closable && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-full p-1.5 text-[var(--lilia-text-muted)] opacity-70 transition-all hover:opacity-100 hover:bg-[var(--lilia-border)] focus:outline-none focus:ring-2 focus:ring-[var(--lilia-ring)]">
            <HiOutlineX className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-left pb-3 border-b border-[var(--lilia-border)]",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 pt-3 border-t border-[var(--lilia-border)]",
        className,
      )}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-[var(--lilia-text)]",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
